"use client";

import { useEffect, useRef, useState } from "react";

/**
 * Reports whether the referenced element is inside the viewport.
 *
 * Replaces `react-visibility-sensor`, which polled `getBoundingClientRect` on a 100ms
 * interval for the life of the page — 30 forced layouts per second on the homepage while
 * idle. An IntersectionObserver reports the same transitions without reading layout.
 *
 * @param {Object} [options]
 * @param {boolean} [options.partial] Report visible once any part of the element intersects, rather than requiring all of it
 * @param {boolean} [options.once] Stop observing after the element first becomes visible
 * @returns {[boolean, React.RefObject]}
 */
export const useIsVisible = ({ partial = false, once = false } = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const element = useRef(null);

  useEffect(() => {
    if (!element.current) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);

        if (entry.isIntersecting && once) {
          observer.disconnect();
        }
      },
      { threshold: partial ? 0 : 1 }
    );

    observer.observe(element.current);

    return () => observer.disconnect();
  }, [partial, once]);

  return [isVisible, element];
};
